import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Building2, ChevronRight, AlertTriangle } from 'lucide-react';
import { useCompaniesWithBillingInfo } from '@/hooks/useCompaniesWithBillingInfo';
import { useState } from 'react';

export function CompanyBillingList() {
  const { data: companies = [], isLoading } = useCompaniesWithBillingInfo();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(cents / 100);
  };

  const filteredCompanies = companies.filter(company => {
    if (!search.trim()) return true;
    const searchLower = search.toLowerCase();
    return (
      company.name.toLowerCase().includes(searchLower) ||
      company.doc_cnpj?.includes(search) ||
      company.plan_name?.toLowerCase().includes(searchLower)
    );
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Empresas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Empresas
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Clique em uma empresa para ver detalhes financeiros
            </p>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar empresa..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Empresa</TableHead>
                <TableHead>Plano</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Faturas</TableHead>
                <TableHead className="text-center">Pendentes</TableHead>
                <TableHead className="text-right">Total Pago</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCompanies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Nenhuma empresa encontrada.
                  </TableCell>
                </TableRow>
              ) : (
                filteredCompanies.map((company) => (
                  <TableRow
                    key={company.id}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => navigate(`/admin/billing/${company.id}`)}
                  >
                    <TableCell>
                      <div className="font-medium">{company.name}</div>
                      {company.doc_cnpj && (
                        <div className="text-xs text-muted-foreground">
                          CNPJ: {company.doc_cnpj}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {company.plan_name ? (
                        <Badge variant="secondary">{company.plan_name}</Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">Sem plano</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={company.status === 'active' ? 'default' : 'outline'}>
                        {company.status === 'active' ? 'Ativo' : company.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {company.total_invoices}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        {company.overdue_count > 0 && (
                          <AlertTriangle className="h-4 w-4 text-destructive" />
                        )}
                        <span className={company.overdue_count > 0 ? 'text-destructive font-medium' : ''}>
                          {company.pending_count}
                        </span>
                        {company.overdue_count > 0 && (
                          <span className="text-xs text-destructive">
                            ({company.overdue_count} vencidas)
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(company.paid_amount_cents)}
                    </TableCell>
                    <TableCell>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
